import { useEffect, useState } from "react";
import dayjs from "dayjs";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import api, { apiError } from "@/lib/api";
import { money, pnlFmt, pnlColor } from "@/lib/format";
import TradesTable from "@/components/TradesTable";
import { toast } from "sonner";

export default function ClientDetailDialog({ client, onClose }) {
  const [trades, setTrades] = useState([]);
  const [txs, setTxs] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!client) return;
    setLoading(true);
    setTrades([]);
    setTxs([]);
    Promise.all([
      api.get(`/admin/clients/${client.id}/trades`),
      api.get(`/admin/clients/${client.id}/transactions`),
    ])
      .then(([t, x]) => {
        setTrades(t.data);
        setTxs(x.data);
      })
      .catch((e) => toast.error(apiError(e)))
      .finally(() => setLoading(false));
  }, [client]);

  const closed = trades.filter((t) => t.status === "closed");
  const pnl = closed.reduce((s, t) => s + (Number(t.profit) || 0), 0);

  return (
    <Dialog open={!!client} onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        data-testid="client-detail-modal"
        className="max-h-[90vh] overflow-y-auto border-[#2A2E39] bg-[#0B0E14] text-[#EAECEF] sm:max-w-6xl"
      >
        <DialogHeader>
          <DialogTitle className="font-heading text-lg">
            {client?.name}{" "}
            <span className="font-mono text-sm text-[#F0B90B]">#{client?.account_no}</span>
          </DialogTitle>
          <DialogDescription className="text-sm text-[#848E9C]">
            {client?.email}
            {client?.phone ? ` · ${client.phone}` : ""} · Balance{" "}
            <span className="font-mono text-[#F0B90B]">{money(client?.balance ?? 0)}</span> · Total P&L{" "}
            <span className={`font-mono ${pnlColor(pnl)}`}>{pnlFmt(pnl)}</span>
          </DialogDescription>
        </DialogHeader>
        {loading ? (
          <div className="flex justify-center py-12" data-testid="client-detail-loading">
            <div className="h-7 w-7 animate-spin rounded-full border-2 border-[#F0B90B] border-t-transparent" />
          </div>
        ) : (
          <div className="space-y-6">
            <div className="space-y-2">
              <h3 className="font-heading text-xs font-semibold uppercase tracking-widest text-[#848E9C]">
                Trade History ({trades.length})
              </h3>
              <TradesTable trades={trades} variant="client" testid="client-detail-trades-table" />
            </div>
            <div className="space-y-2">
              <h3 className="font-heading text-xs font-semibold uppercase tracking-widest text-[#848E9C]">
                Deposits & Withdrawals ({txs.length})
              </h3>
              <div className="overflow-x-auto rounded-lg border border-[#2A2E39] bg-[#131722]">
                <table data-testid="client-detail-ledger-table" className="w-full min-w-[720px] text-sm">
                  <thead>
                    <tr className="border-b border-[#2A2E39]">
                      {["Date", "Transaction ID", "Type", "Method", "Note", "Amount", "Status", "Balance After"].map((h) => (
                        <th key={h} className="whitespace-nowrap px-3 py-2.5 text-left text-[11px] font-medium uppercase tracking-wider text-[#848E9C]">
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {txs.length === 0 && (
                      <tr>
                        <td colSpan={8} className="px-3 py-10 text-center text-sm text-[#5E6673]">
                          No transactions yet
                        </td>
                      </tr>
                    )}
                    {txs.map((t) => (
                      <tr key={t.id} data-testid={`detail-tx-row-${t.id.slice(0, 8)}`} className="border-b border-[#1E222D] transition-colors hover:bg-[#1E222D]/60">
                        <td className="whitespace-nowrap px-3 py-2 font-mono text-xs text-[#848E9C]">
                          {dayjs(t.created_at).format("YYYY.MM.DD HH:mm")}
                        </td>
                        <td className="whitespace-nowrap px-3 py-2 font-mono text-xs text-[#5E6673]">
                          TX-{t.id.slice(0, 8).toUpperCase()}
                        </td>
                        <td className="whitespace-nowrap px-3 py-2">
                          <span
                            className={`rounded px-1.5 py-0.5 font-mono text-[11px] font-bold ${
                              t.type === "deposit"
                                ? "bg-[rgba(14,203,129,0.12)] text-[#0ECB81]"
                                : "bg-[rgba(246,70,93,0.12)] text-[#F6465D]"
                            }`}
                          >
                            {t.type.toUpperCase()}
                          </span>
                        </td>
                        <td className="whitespace-nowrap px-3 py-2 text-[#EAECEF]">{t.method}</td>
                        <td className="max-w-[160px] truncate px-3 py-2 text-xs text-[#5E6673]">{t.note || "—"}</td>
                        <td className={`whitespace-nowrap px-3 py-2 text-right font-mono font-semibold ${t.type === "deposit" ? "text-[#0ECB81]" : "text-[#F6465D]"}`}>
                          {t.type === "deposit" ? "+" : "-"}
                          {money(t.amount)}
                        </td>
                        <td className="whitespace-nowrap px-3 py-2">
                          <span className="rounded border border-[#F0B90B]/40 bg-[#F0B90B]/10 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-[#F0B90B]">
                            {t.status}
                          </span>
                        </td>
                        <td className="whitespace-nowrap px-3 py-2 text-right font-mono text-[#EAECEF]">{money(t.balance_after)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
